import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Store, X, PackageSearch, RefreshCw, Wallet, ReceiptText, Truck } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { ProductGrid } from "../components/shop/ProductCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

// Felles «Butikk»: aktive varer frå alle arrangørar med open butikk. Søk og arrangørfilter skjer lokalt (lista er lita i v1).
export default function ShopMarketplacePage() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [orgFilter, setOrgFilter] = useState("");

  const load = () => {
    setLoading(true);
    setError(false);
    api
      .get("/shop", { params: { limit: 300 } })
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const products = useMemo(() => {
    const list = data?.products || [];
    const needle = q.trim().toLowerCase();
    return list.filter((p) => (!orgFilter || p.organizer_id === orgFilter) && (!needle || `${p.name} ${p.description || ""} ${p.organizer_name || ""}`.toLowerCase().includes(needle)));
  }, [data, q, orgFilter]);

  const organizers = data?.organizers || [];
  const total = data?.products?.length || 0;

  return (
    <PublicLayout>
      {/* Låg hero: tittel + søk + filterchips (design: public_marketplace.hero) */}
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-6">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-3 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand))]"><Store className="w-5 h-5" /></span>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-[hsl(var(--info))] border-[hsl(var(--info))]/40" data-testid="marketplace-kicker">{t("Butikk")}</Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight" data-testid="marketplace-title">{t("Butikk – varer fra arrangørene")}</h1>
            <p className="text-base text-muted-foreground leading-relaxed">{t("Bøker, suvenirer og andre varer fra lokale arrangører. Du handler direkte med arrangøren – betal med Vipps eller få faktura.")}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
              <span className="inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5 text-brand" /> {t("Vipps")}</span>
              <span className="inline-flex items-center gap-1.5"><ReceiptText className="w-3.5 h-3.5 text-brand" /> {t("Faktura")}</span>
              <span className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-brand" /> {t("Henting eller sending i posten")}</span>
            </div>
          </motion.div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-xl">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søk etter vare eller arrangør")} className="pl-9 h-11 bg-background/80" aria-label={t("Søk etter vare eller arrangør")} data-testid="marketplace-search-input" />
              {q && (
                <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150" aria-label={t("Nullstill søk")} data-testid="marketplace-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>
            {!loading && !error && <p className="text-sm text-muted-foreground" data-testid="marketplace-count">{products.length === 1 ? t("{n} vare", { n: 1 }) : t("{n} varer", { n: products.length })}</p>}
          </div>
          {organizers.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="marketplace-organizer-filter">
              <button type="button" onClick={() => setOrgFilter("")} className={`rounded-full border px-3 py-1.5 text-sm transition-colors duration-150 ${!orgFilter ? "bg-[hsl(var(--accent))] text-foreground border-[hsl(var(--brand))]/30" : "bg-card text-muted-foreground border-border hover:bg-accent hover:text-foreground"}`} aria-pressed={!orgFilter} data-testid="marketplace-filter-all">{t("Alle arrangører")}</button>
              {organizers.map((o) => (
                <button key={o.id} type="button" onClick={() => setOrgFilter(o.id === orgFilter ? "" : o.id)} className={`rounded-full border px-3 py-1.5 text-sm transition-colors duration-150 ${orgFilter === o.id ? "bg-[hsl(var(--accent))] text-foreground border-[hsl(var(--brand))]/30" : "bg-card text-muted-foreground border-border hover:bg-accent hover:text-foreground"}`} aria-pressed={orgFilter === o.id} data-testid={`marketplace-filter-${o.id}`}>
                  {o.org_name} <span className="text-xs opacity-70 tabular-nums">({o.product_count})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="marketplace-section">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6" data-testid="marketplace-loading">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
          </div>
        ) : error ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center space-y-3" data-testid="marketplace-error">
            <p className="font-heading font-semibold">{t("Kunne ikke laste butikken")}</p>
            <Button onClick={load} variant="outline" data-testid="marketplace-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" /> {t("Prøv igjen")}</Button>
          </div>
        ) : products.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center space-y-3" data-testid="marketplace-empty">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"><PackageSearch className="w-7 h-7" /></span>
            <h2 className="font-heading font-semibold text-lg">{total === 0 ? t("Ingen varer ennå") : t("Ingen varer passet søket")}</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">{total === 0 ? t("Når arrangører åpner butikken sin, dukker varene opp her.") : t("Prøv et annet søkeord, eller fjern filteret.")}</p>
            {total === 0 ? (
              <Button asChild variant="outline" data-testid="marketplace-empty-events-link"><Link to="/">{t("Se arrangementer")}</Link></Button>
            ) : (
              <Button variant="outline" onClick={() => { setQ(""); setOrgFilter(""); }} data-testid="marketplace-reset-button">{t("Nullstill søk")}</Button>
            )}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <ProductGrid products={products} showOrganizer testId="marketplace-grid" />
          </motion.div>
        )}
      </section>
    </PublicLayout>
  );
}
